import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useParams, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Navbar from './components/Navbar';
import Login from './components/Login';
import Signup from './components/Signup';
import Dashboard from './components/Dashboard';
import EmailAccounts from './components/EmailAccounts';
import CreateCampaign from './components/CreateCampaign';
import CampaignsList from './components/CampaignsList';
import CampaignAnalysis from './components/CampaignAnalysis';
import SummaryCard from './components/SummaryCard';
import TagSection from './components/TagSection';
import Loading from './components/Loading';
import Error from './components/Error';
import WebsiteScraper from './components/WebsiteScraper';
import AdminUserManagement from './components/AdminUserManagement';
import CallDispatcher from './components/CallDispatcher';
import CallHistory from './components/CallHistory';
import TaskManagement from './components/TaskManagement';
import TaskDetail from './components/TaskDetail';
import LandingPage from './components/LandingPage';
import ThankYou from './components/ThankYou';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import About from './components/About';
import HomeNavbar from './components/HomeNavbar';
import { fetchTagResponses } from './services/api';

// Home component - shows content immediately without blocking on auth
function Home() {
  const navigate = useNavigate();
  // Check localStorage directly without using useAuth hook to avoid blocking
  // This ensures Google's crawler can access the page without any delays
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);

  React.useEffect(() => {
    // Check if user is logged in (non-blocking, doesn't wait for API)
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Always show homepage content immediately - never block on loading
  // This ensures Google's crawler can access the page without authentication
  // Logged-in users can also access the homepage
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0e27] to-[#0f172a]">
      <HomeNavbar />
      <div className="px-4 py-12 pt-28">
        <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 mt-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Revenue Validation and Qualification Platform
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-4">
            ConvrsAI by Market Wavegen ensures only real, verified buying intent reaches your sales team.
          </p>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            Most demand systems detect interest. ConvrsAI by Market Wavegen proves buyer reality before sales ever engages.
          </p>
        </div>

        {/* Problem Section */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/30 rounded-xl p-8 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-semibold text-white mb-6">The Problem: Activity Is Not Revenue Reality</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              Modern revenue teams are drowning in leads but starving for certainty. Forms get completed. Content gets downloaded. Signals get triggered. Yet sales conversations begin with friction:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>"I don't remember filling this out."</li>
              <li>Wrong phone numbers</li>
              <li>Students posing as buyers</li>
              <li>Vendors downloading competitor assets</li>
              <li>Junior employees entering enterprise funnels</li>
            </ul>
            <p className="mt-4">
              <strong className="text-white">The problem isn't lead generation. The problem is lead trustworthiness.</strong> ConvrsAI by Market Wavegen fixes this before revenue is exposed.
            </p>
          </div>
        </div>

        {/* Solution Section */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/30 rounded-xl p-8 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-semibold text-white mb-6">What ConvrsAI by Market Wavegen Does</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              ConvrsAI by Market Wavegen is a Revenue Validation Platform and Qualification Engine designed to ensure every lead entering your CRM represents a real buying opportunity. It evaluates each lead as a risk decision not just a record.
            </p>
            <p>
              ConvrsAI by Market Wavegen validates:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">The business</strong> - Company legitimacy, website presence, industry classification</li>
              <li><strong className="text-white">The buyer</strong> - Job title accuracy, role seniority, decision influence</li>
              <li><strong className="text-white">The intent</strong> - AI-led email and voice qualification, conversation intelligence</li>
              <li><strong className="text-white">The readiness</strong> - Firmographic fit, timing indicators, confidence scoring</li>
            </ul>
            <p className="mt-4 font-semibold text-blue-400">
              Before sales touches the lead.
            </p>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/30 rounded-xl p-8 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-semibold text-white mb-6">How ConvrsAI by Market Wavegen Works</h2>
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">Deep Web Lead Validation</h3>
              <p>Validates each lead using deep web search and multiple data sources. Verifies company legitimacy, website presence, industry classification, and geographic relevance. Removes fake companies, dormant businesses, and mismatched industries.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">Firmographic Verification</h3>
              <p>Cross-checks and normalizes company size, revenue band, employee count, and growth indicators. Each lead is matched against your ICP rules. Filters misaligned accounts before they reach sales.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">AI-Led Email & Voice Qualification</h3>
              <p>Runs automated, intent-focused email conversations and places short, AI-led voice calls to confirm interest, validate urgency, capture objections, and identify next steps. Sales receives clarity, not confusion.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">Conversation Intelligence & Confidence Scoring</h3>
              <p>Converts every interaction into intent summaries, objection signals, timing indicators, and sales-ready context. Each lead receives a confidence score based on validation depth, firmographic fit, role relevance, and conversation outcomes.</p>
            </div>
          </div>
        </div>

        {/* Outcome Section */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/30 rounded-xl p-8 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-semibold text-white mb-6">The Outcome: No More Wasted Leads</h2>
          <div className="space-y-4 text-gray-300">
            <p>With ConvrsAI by Market Wavegen:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>No more wrong phone numbers</li>
              <li>No more "I never filled this form"</li>
              <li>No more irrelevant titles in sales queues</li>
              <li>No more SDR time wasted on disqualification</li>
              <li>No more pipelines built on assumptions</li>
            </ul>
            <p className="mt-4 font-semibold text-blue-400">
              Only validated, qualified, sales-ready demand reaches your team.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600/20 to-blue-800/20 border border-blue-500/30 rounded-xl p-8 mb-8 backdrop-blur-sm text-center">
          <h2 className="text-2xl font-semibold text-white mb-4">Turn Demand into Revenue Reality</h2>
          <p className="text-lg text-gray-300 mb-6">
            Stop sending uncertainty to sales. Start sending validated buying intent.
          </p>
          <a
            href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1LRerQzILalOQbfT9l5lWQ6abwrkk6n45IbqR_cPsf8tH09a8kVlButSqrKMTkMo1De-h8vUdB"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-8 py-3 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-lg shadow-blue-600/30"
          >
            Book a Demo
          </a>
        </div>

        {/* Data Usage Explanation */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/30 rounded-xl p-8 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-semibold text-white mb-6">How We Use Your Data</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              To provide our email campaign services, ConvrsAI requires access to your Gmail account. We only request limited Google permissions and use them solely to power features you choose to use. Here's how we use this access:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">Gmail Access (gmail.send only):</strong> We request permission to send emails on your behalf through your connected Gmail account using the Gmail API. This allows you to send marketing campaigns directly from your email address when you run campaigns in ConvrsAI. We do not request access to read, compose, or manage drafts in your Gmail.</li>
              <li><strong className="text-white">Email Account Information:</strong> We access your email address and basic profile information (name and profile image) to identify and manage your connected accounts and display them inside the product.</li>
              <li><strong className="text-white">Campaign Management:</strong> We use your account information to track email campaigns, manage sending limits, and provide analytics on campaign performance.</li>
              <li><strong className="text-white">Data Security:</strong> All data is stored in secure infrastructure with restricted access. We do not sell your Google user data or share it with third parties for their own marketing.</li>
            </ul>
            <p className="mt-4">
              <strong className="text-white">Your Privacy Matters:</strong> We only access the minimum data necessary to provide our services, in line with our{' '}
              <a href="/privacy-policy" className="text-blue-400 hover:text-blue-300 underline transition-colors">
                Privacy Policy
              </a>
              . You can revoke access at any time through your Google account settings or by disconnecting your account in our platform.
            </p>
          </div>
        </div>

        {/* Login/Signup or Dashboard Section */}
        {/* <div className="bg-[#1a1f3a]/80 border border-blue-500/30 rounded-xl p-8 mb-8 backdrop-blur-sm">
          <h2 className="text-2xl font-semibold text-white mb-6 text-center">
            {isLoggedIn ? 'Welcome Back!' : 'Get Started'}
          </h2>
          <div className="space-y-4 max-w-md mx-auto">
            {isLoggedIn ? (
              <>
                <button
                  onClick={() => navigate('/tasks')}
                  className="w-full px-6 py-3 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-lg shadow-blue-600/30"
                >
                  Go to Tasks
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full px-6 py-3 text-base font-medium text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-all border border-gray-700"
                >
                  Go to Dashboard
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-6 py-3 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-lg shadow-blue-600/30"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="w-full px-6 py-3 text-base font-medium text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-all border border-gray-700"
                >
                  Create Account
                </button>
              </>
            )}
          </div>
        </div> */}

        {/* Footer with Legal Links */}
        <footer className="text-center space-y-4 mt-12 pt-8 border-t border-blue-500/20">
          <nav aria-label="Legal links">
            <ul className="flex flex-wrap justify-center gap-4 text-sm list-none p-0 m-0">
              <li>
                <a 
                  href="/privacy-policy" 
                  className="text-blue-400 hover:text-blue-300 underline transition-colors"
                  aria-label="Privacy Policy"
                >
                  Privacy Policy
                </a>
              </li>
              <li className="text-gray-600" aria-hidden="true">•</li>
              <li>
                <a 
                  href="/terms" 
                  className="text-blue-400 hover:text-blue-300 underline transition-colors"
                  aria-label="Terms and Conditions"
                >
                  Terms and Conditions
                </a>
              </li>
            </ul>
          </nav>
          <p className="text-xs text-gray-500">
            By using ConvrsAI by Market Wavegen, you agree to our Terms and Conditions and Privacy Policy. 
            Your use of Gmail integration is subject to Google's Terms of Service.
          </p>
          <p className="text-sm text-gray-400 mt-4">
            © 2026 ConvrsAI by Market Wavegen. All rights reserved.
          </p>
        </footer>
        </div>
      </div>
    </div>
  );
}

// Redirect component for /campaigns/:id to analysis
function CampaignRedirect() {
  const { id } = useParams();
  return <Navigate to={`/campaigns/${id}/analysis`} replace />;
}

// Analytics component (legacy)
function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTagResponses();
      if (!result.success) {
        throw new Error(result.message || 'Failed to load data');
      }
      setData(result);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error message={error} onRetry={loadData} />;
  }

  if (!data || !data.stats || data.stats.length === 0) {
    return (
      <div className="app-container">
        <div className="header">
          <h1>📊 Email Campaign Analytics</h1>
          <p>Tag-wise Response Analysis Dashboard</p>
          <button className="refresh-btn" onClick={loadData}>
            🔄 Refresh Data
          </button>
        </div>
        <div className="no-data">No campaign data available</div>
      </div>
    );
  }

  const { stats, responsesByTag } = data;

  return (
    <div className="app-container">
      <div className="header">
        <h1>📊 Email Campaign Analytics</h1>
        <p>Tag-wise Response Analysis Dashboard</p>
        <button className="refresh-btn" onClick={loadData}>
          🔄 Refresh Data
        </button>
      </div>

      <SummaryCard stats={stats} />

      <div className="tag-sections">
        {stats.map((stat) => (
          <TagSection
            key={stat.tag}
            stat={stat}
            responses={responsesByTag[stat.tag] || []}
          />
        ))}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* <Route path="/" element={<Home />} /> */}
          <Route path="/" element={<Home />} /> 
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <>
                  <Navbar />
                  <Routes>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/email-accounts" element={<EmailAccounts />} />
                    <Route path="/campaigns" element={<CampaignsList />} />
                    <Route path="/campaigns/create" element={<CreateCampaign />} />
                    <Route path="/campaigns/:id/analysis" element={<CampaignAnalysis />} />
                    <Route path="/campaigns/:id" element={<CampaignRedirect />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/scraper" element={<WebsiteScraper />} />
                    <Route path="/calls" element={<CallDispatcher />} />
                    <Route path="/calls/history" element={<CallHistory />} />
                    <Route path="/tasks" element={<TaskManagement />} />
                    <Route path="/tasks/:id" element={<TaskDetail />} />
                    <Route 
                      path="/admin/users" 
                      element={
                        <AdminRoute>
                          <AdminUserManagement />
                        </AdminRoute>
                      } 
                    />
                    <Route path="/dashboard-redirect" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/oauth-error" element={
                      <div style={{ padding: '40px', textAlign: 'center' }}>
                        <h1>OAuth Errors</h1>
                        <p>There was an error connecting your Gmail account. Please try again.</p>
                      </div>
                    } />
                  </Routes>
                </>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
